
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { PMField } from "../../../utils/PMField";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { dispatchWithToast, mapErrors } from "../../../Utils";
import PMSwitch from "../../../utils/PMSwitch";
import {
  createReportAsync,
  updateReportAsync,
} from "../../../../slices/service/reports";
import {
  createReportChecklist,
  deleteReportChecklist,
  fetchReport,
  fetchReportChecklist,
} from "../../../../api/service/report";
import { QuillEditor } from "../../../utils/quill-editor";
import { DeliveryAddressSelector } from "../../../utils/moduleSelectors/deliveryAddressSelector";
import { SystemSelector } from "../../../utils/moduleSelectors/systemSelector";
import { ProjectSelector } from "../../../utils/moduleSelectors/projectSelector";
import { CustomerSelector } from "../../../utils/moduleSelectors/customerSelector";
import { ReportTypeSelector } from "../../../utils/moduleSelectors/reportTypeSelector";
import ReportItemConsumptionList from "./report-item-consumption-list";
import ReportPriceLinesList from "./report-price-lines-list";
import { ReportPriceLinesSection } from "./ReportPriceLinesSection";
import PMSteps from "../../../utils/PMSteps";
import ActivitiesTable from "../../activities/activitiesTable";
import { ProjectFolderSelector } from "../../../utils/moduleSelectors/projectFolderSelector";
import ChecklistItemLines from "../../quality/checklists/checklistItemLines";
import { PMNotice } from "../../../utils/notifications";
import { PMDialog } from "../../../utils/PMDialog";
import { ContactsSelector } from "../../../utils/moduleSelectors/contactSelector";
import { PMButton } from "../../../utils/PMButton";
import { TemplateDialogButton } from "../../templates/template-dialog-button";
import SimplePrompt from "../../../utils/simplePrompt";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { DocumentIcon } from "@heroicons/react/24/outline";
import { DocumentUploadSectionV5 } from "../../Media/DocumentUploadSectionV5";
import DocumentsDialog from "../../documents/documents-dialog";
import { EnhancedChecklistSelector } from "./enhanced-checklist-selector";

/**
 * Extra GET /reports/:id/checklist is only needed when the checklist payload is incomplete.
 * Default report JSON already embeds checklist with items — do not refetch on the Checklist tab in that case.
 */
function checklistNeedsFullFetch(checklist) {
  if (!checklist?.id) return false;
  if (Array.isArray(checklist.items)) return false;
  return !Object.prototype.hasOwnProperty.call(checklist, "is_items_editable");
}

export default function ServiceReportEditModal({ redirect = true, ...props }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    report: {},
  });

  const {
    open,
    onClose,
    selected,
    fields,
    project,
    customer,
    report_type,
    contact,
    delivery_address,
  } = props;

  const { report, isLoading, error, errors } = itemState;

  const [isNew, setIsNew] = useState(false);

  // Set isLoading on dispatches
  useEffect(() => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
  }, [dispatch]);

  useEffect(() => {
    if (!report?.id) {
      setIsNew(true);
    } else {
      setIsNew(false);
    }
  }, [report]);

  const getReport = async () => {
    await fetchReport(selected.id)
      .then((res) => {
        if (res.data.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            report: res.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          report: {},
          error: true,
          errors: errors,
        }));
      });
  };

  const createOrUpdateDispatch = (data) => {
    if (report?.id) {
      const res = dispatch(updateReportAsync(data));
      return res;
    } else {
      const res = dispatch(createReportAsync(data));
      return res;
    }
  };

  const [currentStageIdx, setCurrentStageIdx] = useState(0);

  const defaultStages = [
    {
      id: 1,
      title: t("Details"),
      description: t("report.details.description"),
      current: true,
      completed: false,
      active: true,
    },
    {
      id: 2,
      title: t("Items"),
      description: t("report.items.description"),
      current: false,
      completed: false,
      active: report?.report_type?.items ? true : false,
    },
    {
      id: 3,
      title: t("report.price_lines.title"),
      description: t("report.price_lines.description"),
      current: false,
      completed: false,
      active: true,
    },
    {
      id: 4,
      title: t("Checklist"),
      description: t("report.checklist.description"),
      current: false,
      completed: false,
      active: report?.report_type?.checklist ? true : false,
    },
  ];
  const [stages, setStages] = useState(defaultStages);

  const activeStages = stages?.filter((stage) => stage.active === true);

  const [stage, setStage] = useState(defaultStages[0]);


  const activePageChecker = (stage) => {
    // Check if active page, else set next page
    if (stage.active) {
      const thisStageId = stage.id;
      const currentStageIdx = activeStages.findIndex(
        (stage) => stage.id === thisStageId
      );
      setStage((prevState) => stage);
      setCurrentStageIdx(currentStageIdx);
    } else {
      // create a loop that goes to next stage until it finds an active stage
      let nextStage = stage.id;
      while (nextStage < activeStages.length) {
        if (activeStages[nextStage].active) {
          setStage((prevState) => activeStages[nextStage - 1]);
          // idx of current "nextStage"
          const nextStageIdx = activeStages.findIndex(
            (stage) => stage.id === nextStage
          );
          setCurrentStageIdx(nextStage - 1);
          break;
        } else {
          nextStage++;
        }
      }
    }
  };

  useEffect(() => {
    if (report?.report_type) {
      updateActiveStages(report?.report_type);
    }
  }, [report]);

  // Ensure price_lines is initialized correctly when report changes
  useEffect(() => {
    if (report?.price_lines && Array.isArray(report.price_lines)) {
      // This will be handled by Formik's enableReinitialize prop
      // No need to manually set here
    }
  }, [report?.price_lines]);

  const updateActiveStages = (reportType) => {
    if (reportType) {
      const updatedStages = stages.map((stage) => {
        if (stage.id === 2) {
          stage.active = reportType?.items ? true : false;
        }
        if (stage.id === 4) {
          stage.active = reportType?.checklist ? true : false;
        }
        return stage;
      });
      setStages(updatedStages);
    }
  };

  const handleNextPage = (formik) => {
    if (currentStageIdx !== activeStages?.length - 1) {
      const nextStage = activeStages[currentStageIdx + 1];
      activePageChecker(nextStage);
    }
    formik.submitForm();
  };

  useEffect(() => {
    if (open) {
      setCurrentStageIdx(0);
      setRefresh(false);
      setStage(defaultStages[0]);
      setItemState((prevState) => ({
        ...prevState,
        isLoading: true,
      }));
      if (selected?.id) {
        getReport();
      } else {
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          report: selected && typeof selected === "object" ? { ...selected } : {},
        }));
      }
    } else {
      setItemState((prevState) => ({
        ...prevState,
        report: {},
      }));
    }
  }, [dispatch, open]);

  function onOtherInvoiceCustomerChange(e, formik) {
    if (!e.target.checked) {
      formik.setFieldValue("invoice_customer_id", null);
    }
  }

  const [selectedReportDocumentId, setSelectedReportDocumentId] =
    useState(null);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);

  const stageOne = (report, formik) => {
    return (
      <div className="grid grid-cols-6 gap-6 mt-10">
        <div className="col-span-3 sm:col-span-3">
          <PMSwitch
            name="invoice_to_project_customer"
            label={t("Invoice to Project Customer")}
            {...formik}
          />
        </div>
        <div className="col-span-3 sm:col-span-3">
          <PMSwitch
            name="other_invoice_customer"
            label={t("Other invoice customer")}
            onChange={(e) => onOtherInvoiceCustomerChange(e, formik)}
            {...formik}
          />
        </div>
        <div className="col-span-3 sm:col-span-3">
          <PMSwitch
            name="cost_included_in_project"
            label={t("Cost included in project")}
            {...formik}
          />
        </div>
        <div className="col-span-3 sm:col-span-3">
          <PMSwitch
            name="sales_income_included_in_project"
            label={t("Sales income included in project")}
            {...formik}
          />
        </div>
        <div className="col-span-3 sm:col-span-3">
          <PMSwitch
            name="do_not_expect_income"
            label={t("Do not expect income")}
            {...formik}
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <PMField
            name="report_number"
            type="text"
            disabled
            label={t("Report Number")}
            {...formik}
          />
        </div>

        <div className="col-span-6 sm:col-span-3">
          <PMField
            name="order_date"
            type="date"
            label={"Ordredato"}
            {...formik}
          />
        </div>

        <div className="col-span-6 sm:col-span-3">
          <CustomerSelector
            label="Kunde"
            selected={report?.customer?.id ? report?.customer : customer}
            {...formik}
          />
        </div>

        <div className="col-span-6 sm:col-span-3">
          <ContactsSelector
            label="Kontaktperson"
            selected={report?.contact?.id ? report?.contact : contact}
            customer={report?.customer?.id ? report?.customer : customer}
            {...formik}
          />
        </div>

        {formik.values.other_invoice_customer && (
          <div className="col-span-6 sm:col-span-3">
            <CustomerSelector
              label="Faktura kunde"
              name="invoice_customer_id"
              selected={report?.invoice_customer}
              {...formik}
            />
          </div>
        )}

        <div className="col-span-6 sm:col-span-3">
          <ReportTypeSelector
            label="Rapport Type"
            selected={report?.report_type}
            {...formik}
          />
        </div>

        <div className="col-span-6 sm:col-span-3">
          <ProjectSelector
            label="Prosjekt"
            selected={report?.project?.id ? report?.project : project}
            {...formik}
          />
        </div>

        <div className="col-span-6 sm:col-span-3">
          <ProjectFolderSelector
            label="Prosjektmappe"
            projectId={formik.values?.project_id || project?.id}
            selected={report?.folder?.id ? report?.folder : null}
            {...formik}
          />
        </div>

        <div className="col-span-6 sm:col-span-6">
          <div className="flex justify-between">
            <label
              htmlFor="name"
              className="font-medium text-gray-700 flex flex-col"
            >
              <label className="text-md">Beskrivelse</label>
              <span className="text-sm italic">
                Husk at denne beskrivelsen er synlig for alle (også kunden)
              </span>
            </label>
            <div>
              <TemplateDialogButton fieldName="description" {...formik} />
            </div>
          </div>
          <QuillEditor name="description" rows={4} {...formik} />
        </div>

        <div className="col-span-6 sm:col-span-6">
          <div className="flex justify-between">
            <label
              htmlFor="name"
              className="font-medium text-gray-700 flex flex-col"
            >
              <label className="text-md">Intern beskrivelse</label>
              <span className="text-sm italic">
                Denne beskrivelsen er kun synlig for ansatte
              </span>
            </label>
            <div>
              <TemplateDialogButton
                fieldName="internal_description"
                {...formik}
              />
            </div>
          </div>

          <QuillEditor name="internal_description" rows={4} {...formik} />
        </div>

        <div className="flex col-span-6 sm:col-span-6">
          {/* Upload Documents Button */}
          <DocumentUploadSectionV5
            multiple={true}
            key={`upload-${report?.id}`}
            disabled={isLoading}
            module="report_description"
            moduleParam={report?.id}
            parentCallback={(data) => formik.setFieldValue("documents", data)}
            buttonStyle="compact"
          />

          {/* View Documents Button with Counter */}
          <div className="ml-2 relative">
            <button
              type="button"
              onClick={() => {
                setSelectedReportDocumentId(report?.id);
                setDocumentsDialogOpen(true);
              }}
              className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors duration-200 rounded hover:bg-blue-50 border border-blue-200 bg-blue-50"
              title={`View Documents (${report?.documents?.length || 0})`}
            >
              <DocumentIcon className="h-4 w-4" />
              {report?.documents?.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {report?.documents?.length}
                </span>
              )}
            </button>
          </div>
        </div>

        <DocumentsDialog
          open={documentsDialogOpen}
          onClose={() => setDocumentsDialogOpen(false)}
          module="report_description"
          moduleParam={selectedReportDocumentId}
          onRefresh={props.onRefresh}
        />

        <div className="col-span-6 sm:col-span-6">
          <div className="flex">
            <PMSwitch
              label="Bruk kundens adresse"
              name="useCustomerAddress"
              onChange={(e) => {
                formik.setFieldValue("delivery_address_id", null);
              }}
              {...formik}
            />
            <span className="ml-2">
              {report?.customer?.postal_address && (
                <>({report?.customer?.postal_address.displayTitle})</>
              )}
            </span>
          </div>
        </div>

        {formik.values.useCustomerAddress && report?.customer?.id ? null : (
          <div className="col-span-6 sm:col-span-3 mb-20">
            <DeliveryAddressSelector
              label="Oppdragsadresse"
              module="CUSTOMER"
              module_param={formik.values.customer_id}
              fields={
                fields || {
                  name: report?.customer?.name,
                }
              }
              selected={report?.delivery_address || delivery_address}
              {...formik}
            />
          </div>
        )}

        <div className="col-span-6 sm:col-span-3 mb-20">
          <SystemSelector
            label="Anlegg"
            module="CUSTOMER"
            module_param={
              report?.customer?.id
                ? report?.customer?.id
                : formik.values.customer_id
            }
            fields={fields}
            selected={report?.system}
            {...formik}
          />
        </div>
      </div>
    );
  };

  const stageTwo = (report, formik) => {
    return (
      <div className="grid grid-cols-6 gap-6 mt-10">
        <div className="col-span-6 sm:col-span-6 overflow-auto">
          <ReportItemConsumptionList
            items={formik.values.items || []}
            onItemsChange={(updatedItems) => {
              formik.setFieldValue("items", updatedItems);
            }}
            disabled={false}
            report={report}
            onSaveItem={async (updatedItem) => {
              // Update the item in formik values
              const currentItems = formik.values.items || [];
              const updatedItems = currentItems.map((item) =>
                item.id === updatedItem.id ? updatedItem : item
              );
              formik.setFieldValue("items", updatedItems);
              
              // Save to backend
              const reportData = {
                id: report.id,
                items: updatedItems,
              };
              
              await dispatchWithToast(
                dispatch(updateReportAsync(reportData)),
                (updatedReport) => {
                  setItemState((prevState) => ({
                    ...prevState,
                    report: {
                      ...prevState.report,
                      ...updatedReport,
                      items: updatedItems,
                    },
                  }));
                }
              );
            }}
          />
        </div>
      </div>
    );
  };

  const stageThree = (report, formik) => {
    if (report?.id) {
      // Use the new section component when report is saved
      return (
        <div className="mt-10">
                  <ReportPriceLinesSection
                    reportId={report.id}
                    project={report?.project}
                  />
        </div>
      );
    } else {
      // Use the old list component for new reports (before saving)
      const priceLines = formik?.values?.price_lines || report?.price_lines || [];
      
      return (
        <div className="grid grid-cols-6 gap-6 mt-10">
          <div className="col-span-6 sm:col-span-6 overflow-auto">
            <ReportPriceLinesList
              priceLines={Array.isArray(priceLines) ? priceLines : []}
              project={report?.project}
              vatTypes={[]} // Will be fetched internally
              onPriceLinesChange={(updatedPriceLines) => {
                if (formik?.setFieldValue) {
                  formik.setFieldValue("price_lines", updatedPriceLines);
                }
              }}
              disabled={false}
            />
          </div>
        </div>
      );
    }
  };



  const [checklistSelectorOpen, setChecklistSelectorOpen] = useState(false);
  const [selectedChecklistConfig, setSelectedChecklistConfig] = useState(null);

  const submitCreateChecklist = async (config) => {
    try {
      let payload = {
        report: {
          id: report?.id,
        },
        fields: "id,checklist",
      };

      if (config) {
        if (config.mode === "type") {
          payload.report.checklist_type_id = config.checklist_type_id;
          if (config.selected_item_ids && config.selected_item_ids.length > 0) {
            payload.report.selected_item_ids = config.selected_item_ids;
          }
        } else if (config.mode === "main_report" && config.items) {
          payload.report.copy_from_checklist_items = config.items;
        }
        // If mode === "new", just create empty checklist (no additional params needed)
      }

      const res = await createReportChecklist(payload);
      
      if (res.data.id && res.data.checklist) {
        // Update only checklist
        setItemState((prevState) => ({
          ...prevState,
          report: {
            ...prevState.report,
            checklist: res.data.checklist,
          },
        }));
        setChecklistSelectorOpen(false);
        setSelectedChecklistConfig(null);
      }
    } catch (err) {
      console.error(err);
      toast.error("Kunne ikke opprette sjekkliste");
    }
  };

  const handleChecklistSelect = (config) => {
    setSelectedChecklistConfig(config);
    submitCreateChecklist(config);
  };

  const deleteChecklist = async () => {
    await deleteReportChecklist({
      id: report?.id,
    })
      .then((res) => {
        if (res.status === 200) {
          setItemState((prevState) => ({
            ...prevState,
            report: {
              ...prevState.report,
              checklist: null,
            },
          }));
          setDeleteChecklistPrompt(false);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const [deleteChecklistPrompt, setDeleteChecklistPrompt] = useState(false);

  const [checklistLoadError, setChecklistLoadError] = useState(false);

  useEffect(() => {
    if (Number(stage?.id) !== 4) {
      setChecklistLoadError(false);
    }
  }, [stage?.id]);

  useEffect(() => {
    if (
      !open ||
      Number(stage?.id) !== 4 ||
      !report?.id ||
      !report?.checklist?.id
    ) {
      return;
    }
    if (!checklistNeedsFullFetch(report.checklist)) {
      return;
    }
    if (checklistLoadError) {
      return;
    }

    let cancelled = false;

    fetchReportChecklist(report.id)
      .then((data) => {
        if (cancelled) return;
        if (data?.errors) {
          toast.error(t("report.checklist.load_error"));
          setChecklistLoadError(true);
          return;
        }
        if (data?.id) {
          const checklist = {
            ...data,
            items: Array.isArray(data.items) ? data.items : [],
          };
          setItemState((prev) => ({
            ...prev,
            report: {
              ...prev.report,
              checklist,
            },
          }));
          setChecklistLoadError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error(t("report.checklist.load_error"));
          setChecklistLoadError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    stage?.id,
    report?.id,
    report?.checklist?.id,
    report?.checklist?.is_items_editable,
    checklistLoadError,
    t,
  ]);

  const canDeleteChecklist = useUserHasAccess({
    role: "report",
    action: "delete_checklist",
  });

  const stageFive = (report, formik) => {
    if (report?.checklist?.id) {
      const needsChecklistDetail = checklistNeedsFullFetch(report.checklist);
      if (needsChecklistDetail && checklistLoadError) {
        return (
          <div className="grid grid-cols-6 gap-6 mt-10">
            <div className="col-span-6 sm:col-span-6">
              <PMNotice
                title={t("report.checklist.load_error_title")}
                description={t("report.checklist.load_error_hint")}
              />
            </div>
          </div>
        );
      }
      if (needsChecklistDetail) {
        return (
          <div className="grid grid-cols-6 gap-6 mt-10">
            <div className="col-span-6 sm:col-span-6 min-h-[200px] flex flex-col items-center justify-center gap-3">
              <p className="text-sm text-gray-600" aria-live="polite">
                {t("report.checklist.loading")}
              </p>
              <ResourceLoading />
            </div>
          </div>
        );
      }
      return (
        <div className="grid grid-cols-6 gap-6 mt-10">
          <div className="col-span-6 sm:col-span-6">
            {canDeleteChecklist && (
              <>
                <div className="flex justify-end mb-10">
                  <PMButton
                    type="button"
                    onClick={() => setDeleteChecklistPrompt(true)}
                    className="max-w-[150px] bg-red-400 right-0"
                    tooltip="Sletter sjekklisten"
                  >
                    Slett sjekkliste
                  </PMButton>
                </div>
                <SimplePrompt
                  title="Sletter sjekklisten"
                  description="Dette kan ikke reverseres. Er du sikker på at du vil slette sjekklisten?"
                  onAccept={() => deleteChecklist()}
                  open={deleteChecklistPrompt}
                  onClose={() => setDeleteChecklistPrompt(false)}
                />
              </>
            )}

            <ChecklistItemLines
              checklist={report?.checklist}
              items={report?.checklist?.items}
              editable={true}
              isLoading={isLoading}
              drag={true}
              addRow={true}
              deleteRow={true}
              deviation={false}
              disabled={
                report?.checklist?.is_items_editable !== undefined &&
                !report?.checklist?.is_items_editable
              }
              type="edit"
              parentCallback={(data) =>
                formik.setFieldValue("checklist.items", data)
              }
            />
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-6 gap-6 mt-10">
        <div className="col-span-6 sm:col-span-6">
          <>
            <div className="py-5 px-5 rounded-md shadow-xl mb-20">
              <PMNotice
                title="Sjekkliste mangler"
                description="Rapporten er ikke knyttet til sjekkliste"
              />

              <div className="mt-4">
                <PMButton
                  type="button"
                  onClick={() => setChecklistSelectorOpen(true)}
                  className="w-full"
                  tooltip="Velg hvordan sjekklisten skal opprettes"
                >
                  Velg sjekkliste
                </PMButton>
              </div>
            </div>
          </>
        </div>
      </div>
    );
  };

  const [refresh, setRefresh] = useState(false);

  const handleFinish = (formik) => {
    formik.submitForm();
    setRefresh(true);
  };

  const handleOnRefresh = (data) => {
    setItemState((prevState) => ({
      ...prevState,
      report: data,
    }));

    setTimeout(() => {
      setItemState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }, 500);

    if (refresh) {
      if (props.onRefresh) {
        props.onRefresh(data);
      }

      onClose(true);

      if (data?.id && redirect) {
        setTimeout(() => {
          // await ID, then navigate
          navigate(`/dashboard/service/reports/${data?.id}`);
        }, 200);
      }
    }
  };

  const handleStageChange = (stage) => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    setTimeout(() => {
      setItemState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }, 250);

    activePageChecker(stage);
  };

  return (
    <PMDialog
      title={report?.id ? t("report.update") : t("report.create")}
      open={open}
      onClose={onClose}
      size="5xl"
      maxWidth="7xl"
    >
      {!isLoading && (
        <div>
          {stages && (
            <PMSteps
              steps={activeStages}
              selected={stage}
              onChange={handleStageChange}
            />
          )}
        </div>
      )}

      {isLoading ? (
        <ResourceLoading />
      ) : (
        <Formik
          enableReinitialize
          initialValues={{
            id: report?.id ? report?.id : null,
            customer_id: report?.customer
              ? report?.customer.id
              : customer?.id
              ? customer?.id
              : null,
            contact_id: report?.contact
              ? report?.contact.id
              : contact?.id
              ? contact?.id
              : null,
            report_type: report?.report_type
              ? report?.report_type.id
              : report_type?.id
              ? report_type?.id
              : null,
            project_id: report?.project
              ? report?.project.id
              : project?.id
              ? project?.id
              : null,
            order_date: report?.order_date
              ? report?.order_date
              : new Date().toISOString().substring(0, 10),
            description: report?.description
              ? report?.description
              : fields?.description
              ? fields?.description
              : null,
            folder_id: report?.folder?.id || null,
            checklist_type_id: report?.checklist?.checklist_type?.id || null,
            checklist: report?.checklist ?? null,
            items: report?.items,
            price_lines: report?.price_lines || [],
            invoice_to_project_customer: report?.invoice_to_project_customer,
            activity_items: report?.activity_items,
            report_number: report?.report_number,
            internal_description: report?.internal_description,
            useCustomerAddress: report?.useCustomerAddress,
            delivery_address_id:
              report?.delivery_address?.id || delivery_address?.id,
            other_invoice_customer: report?.invoice_customer?.id ? true : false,
            cost_included_in_project: report?.cost_included_in_project || false,
            sales_income_included_in_project: report?.sales_income_included_in_project || false,
            do_not_expect_income: report?.do_not_expect_income || false,
            sequel_main_report_id: report?.sequel_main_report_id || report?.sequelMainReport?.id || null,
            submit: null,
          }}
          validationSchema={Yup.object({
            customer_id: Yup.string()
              .nullable()
              .required(t("customer.required")),
            report_type: Yup.string()
              .nullable()
              .required(t("report.type.required")),
            description: Yup.string(),
            project_id: Yup.string().nullable().required(t("project.required")),
          })}
          onSubmit={(values, helpers) => {
            try {
              dispatchWithToast(
                createOrUpdateDispatch(values),
                handleOnRefresh
              );
              helpers.setStatus({ success: true });
              helpers.setSubmitting(false);
            } catch (err) {
              toast.error("Something went wrong...");
              console.error(err);
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: err.message });
              helpers.setSubmitting(false);
            }
          }}
        >
          {(formik) => {
            const checklistStillLoading =
              Number(stage?.id) === 4 &&
              Boolean(report?.checklist?.id) &&
              checklistNeedsFullFetch(report.checklist) &&
              !checklistLoadError;
            const saveDisabled =
              formik.isSubmitting || checklistStillLoading;

            return (
              <Form>
                {stage?.id === 1 && stageOne(report, formik)}
                {stage?.id === 2 && stageTwo(report, formik)}
                {stage?.id === 3 && stageThree(report, formik)}
                {Number(stage?.id) === 4 && stageFive(report, formik)}

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-3 sm:gap-3 justify-between">
                <div className="w-full col-span-2">
                  <button
                    type="button"
                    className="justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                    onClick={onClose}
                  >
                    {"Steng"}{" "}
                  </button>
                </div>

                <div className="flex w-full col-span-1 right align-right gap-4 justify-end">
                  <button
                    type="button"
                    disabled={saveDisabled}
                    className="align-right justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed"
                    onClick={() => handleFinish(formik)}
                  >
                    {report?.id ? "Oppdater" : "Opprett"}
                    {" & Steng"}
                  </button>
                  {currentStageIdx !== activeStages?.length - 1 && (
                    <button
                      type="button"
                      className="align-right justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                      onClick={() => handleNextPage(formik)}
                    >
                      Neste
                    </button>
                  )}
                </div>
              </div>
              </Form>
            );
          }}
        </Formik>
      )}

      <EnhancedChecklistSelector
        open={checklistSelectorOpen}
        onClose={() => {
          setChecklistSelectorOpen(false);
          setSelectedChecklistConfig(null);
        }}
        onSelect={handleChecklistSelect}
        mainReportChecklist={report?.sequelMainReport?.checklist || null}
        mainReport={report?.sequelMainReport || null}
      />
    </PMDialog>
  );
}
